// ============================================
// Enums
// ============================================

export enum DeviceType {
  Relay = 'relay',
  DoubleRelay = 'double_relay',
  TripleRelay = 'triple_relay',
  Dimmer = 'dimmer',
  DoubleDimmer = 'double_dimmer',
  ThermostaticRadiatorValve = 'thermostatic_radiator_valve',
  ClimateSensor = 'climate_sensor',
  DoorSensor = 'door_sensor',
  MotionSensor = 'motion_sensor',
  WaterLeakSensor = 'water_leak_sensor',
  SmokeSensor = 'smoke_sensor',
  GasSensor = 'gas_sensor',
  IrRemoteController = 'ir_remote_controller',
  IrDevice = 'ir_device',
  WireDevice = 'wire_device',
  RollerDriver = 'roller_driver',
  Socket = 'socket',
  None = ''
}

export enum ParameterType {
  Enum = 'enum',
  Bool = 'bool',
  BoolEnum = 'bool_enum',
  Numeric = 'numeric',
  None = ''
}

export enum DeviceActionType {
  DeviceTrigger = 'device_trigger',
  ClickPlay = 'click_play',
  Notification = 'notification',
  None = ''
}

export enum ConditionType {
  AndCondition = 'and_condition',
  OrCondition = 'or_condition',
  None = 'none'
}

export enum TypeAction {
  ManualStart = 'manual_start',
  TimeCondition = 'time_condition',
  Notification = 'notification',
  DeviceCondition = 'device_condition',
  ZigbeeAction = 'zigbee_action',
  None = 'none'
}

export enum AlarmType {
  Waterleak = 'waterleak',
  Security = 'security',
  Fire = 'fire',
  None = 'none'
}

export enum Weekday {
  Mon = 'mon',
  Tue = 'tue',
  Wed = 'wed',
  Thu = 'thu',
  Fri = 'fri',
  Sat = 'sat',
  Sun = 'sun'
}

export enum StatusResult {
  SUCCESS = 0,
  DEVICE_NOT_FOUND = 1,
  FORBIDDEN_DELETE = 7,
  REQUEST_ERROR = 16,
  CONTROLLER_ERROR = 17,
  CONTROLLER_TIMEOUT = 100,
  CONTROLLER_NO_FOUND = 101,
  NONE = -1
}

export enum DeviceStateType {
  Success = 'Success',
  Standard = 'Standard',
  Error = 'Error',
  None = 'None'
}

export enum ComparisonType {
  MORE = 'MORE',
  LESS = 'LESS',
  EQUALS = 'EQUALS'
}

// ============================================
// Core Models
// ============================================

export interface Device {
  id: string;
  baseName: string;
  customName: string | null;
  deviceId: string;
  type: DeviceType;
  room: string;
  isOnline: boolean;
  parameters: Parameter[];
  rusType: string | null;
  flatId: number | null;
  index: number;
  deviceState: DeviceState | null;
}

export interface Parameter {
  id: string; // Computed from name
  name: string;
  rusName: string | null;
  type: ParameterType;
  stringValue: string | null;
  boolValue: boolean | null;
  numericValue: number | null;
  rusValueOnlyView: string | null;
  readableValue?: string | null;
}

export interface DeviceState {
  parameterName: string | null;
  rusValueOnlyView: string | null;
  state: DeviceStateType;
}

// ============================================
// Script Models
// ============================================

export interface DeviceAction {
  id: string; // Computed from actionId
  actionId: string;
  name: string | null;
  type: DeviceActionType;
  isActive: boolean;
  ifDevices: IfThenDevice[] | null;
  thenDevices: IfThenDevice[];
  groupDeviceId: string;
  groupName: string;
  flatId: number;
  conditionType: ConditionType;
}

export interface IfThenDevice {
  id: string; // UUID
  deviceId: string | null;
  deviceName: string | null;
  deviceType: DeviceType;
  room: string | null;
  params: IfThenDeviceParams;
}

export interface IfThenDeviceParams {
  comparison: ComparisonType | null;
  parameterName: string | null;
  parameterNameRus: string | null;
  rusValueOnlyView: string | null;
  description: string | null;
  paramType: ParameterType;
  typeAction: TypeAction;
  time: string | null;
  stringValue: string | null;
  boolValue: boolean | null;
  numericValue: number | null;
  daysOfWeek: string[] | null;
}

export interface ScriptsData {
  status: number | null;
  scriptList: DeviceAction[];
}

export interface ScriptsDataSingle {
  status: number | null;
  script: DeviceAction | null;
}

// ============================================
// Safety Models
// ============================================

export interface SafetySettings {
  status: number;
  info: string | null;
  resetData: ResetData | null;
  isEnabled: boolean | null;
  deviceList: Device[] | null;
}

export interface ResetData {
  weekDay: Weekday | null;
  time: string;
  weekDayTitle?: string | null;
}

export interface SafetyList {
  showSettings: boolean | null;
  securitySettings: SafetySettings;
  flatId: number | null;
}

// ============================================
// Settings Models
// ============================================

export interface SettingsSmartHome {
  showSettings: boolean | null;
}

export interface AllowRemoteControl {
  isEnabled: boolean | null;
  description: string | null;
  expiresAt: number | null;
  flatId: number | null;
  flatAddress: string | null;
}

// ============================================
// Pairing Models
// ============================================

export interface PairingDeviceList {
  devices: PairingDevice[] | null;
}

export interface PairingDevice {
  id: string | null;
  name: string | null;
  type: string | null;
  deviceId: string;
}

// ============================================
// Statistics Models
// ============================================

export interface DeviceStatistic {
  deviceId: string | null;
  parameterName: string | null;
  startTime: string | null;
  endTime: string | null;
  periodType: number | null;
  data: StatisticDataPoint[] | null;
}

export interface StatisticDataPoint {
  timestamp: string | null;
  value: number | null;
  stringValue: string | null;
}

// ============================================
// Response Models
// ============================================

export interface StatusResultCode {
  status: StatusResult | null;
  isSuccess: boolean;
}

export interface StatusData {
  status: number | null;
  message: string | null;
  isSuccess: boolean;
}

export interface IdTitle {
  id: number;
  title: string;
}

// ============================================
// Helper Models
// ============================================

export interface IfPicker {
  id: number | null;
  pickerId: number | null;
  title: string;
  type: IfPickerType;
}

export enum IfPickerType {
  StateDevice = 0,
  Manual = 1,
  Time = 2
}

export interface ThenPicker {
  id: number | null;
  pickerId: number | null;
  title: string;
  type: ThenPickerType;
}

export enum ThenPickerType {
  ChangeState = 0,
  SendNotification = 1
}

export interface Comparison {
  id: number | null;
  compareId: number | null;
  title: string;
  type: ComparisonType;
}

// ============================================
// Real-time Models
// ============================================

export interface RealTimeMessage {
  type: 'deviceUpdate' | 'safetyAlert' | 'scriptTriggered' | 'connectionStatus';
  deviceId?: string;
  parameter?: string;
  value?: any;
  timestamp?: number;
  alertType?: string;
  message?: string;
  isConnected?: boolean;
}

// ============================================
// UI Helper Types
// ============================================

export interface DeviceGroup {
  key: string | null;
  value: Device[];
}

export interface Room {
  id: number;
  title: string;
}

export type TabType = 'devices' | 'scripts' | 'safety';

export interface ToastMessage {
  id: string;
  message: string;
  type: 'success' | 'error' | 'info';
}

export interface NavigationState {
  stack: string[];
  currentTitle: string;
  showBack: boolean;
}

// ============================================
// API Method Types
// ============================================

export interface APIResponse<T> {
  data: T;
  status: number;
  message?: string;
}

export interface DeviceValueParams {
  flatId: number;
  deviceId: string;
  parameter: Parameter;
  showLoader?: boolean;
}

export interface EditDeviceParams {
  flatId: number;
  deviceId: string;
  newName?: string;
  newRoom?: string;
}

export interface ScriptParams {
  flatId: number;
  showLoader?: boolean;
}

export interface SafetySettingsParams {
  flatId?: number;
  group: AlarmType;
  showLoader?: boolean;
}

export interface PairingParams {
  flatId?: number;
  value?: boolean;
  showLoader?: boolean;
}

export interface DevicePairingParams {
  flatId?: number;
  deviceId?: string;
  name?: string;
  room?: string;
}

export interface StatisticParams {
  flatId: number;
  deviceId: string;
  parameterName: string;
  startTime: Date;
  endTime: Date;
  showLoader?: boolean;
}

// ============================================
// Configuration Types
// ============================================

export interface SmartHomeConfig {
  api: APIConfig;
  realTime: RealTimeConfig;
  ui: UIConfig;
  logging: LoggingConfig;
}

export interface APIConfig {
  baseURL: string;
  timeout: number;
  enableLogging: boolean;
}

export interface RealTimeConfig {
  useSignalR: boolean;
  pollingInterval: number;
  reconnectAttempts: number;
  reconnectDelay: number;
}

export interface UIConfig {
  showDeviceIcons: boolean;
  showRoomNames: boolean;
  groupByRoom: boolean;
  animateChanges: boolean;
}

export interface LoggingConfig {
  isEnabled: boolean;
  logLevel: 'debug' | 'info' | 'warning' | 'error' | 'none';
  includeAPIRequests: boolean;
  includeAPIResponses: boolean;
}
